import cx from 'classnames';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { GovBackdrop, GovIcon } from '@gov-design-system-ce/react';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	DataBoxFolderType,
	useDataBoxesContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { Button } from '@gov-nx/ui/web';
import { useTitle } from '@gov-nx/utils/web';
import { pageRouteResolver } from '@gov-nx/web';
import { DataBoxStorageUsage } from './DataBoxStorageUsage';
import { DataBoxesList } from './DataBoxesList';
import { DataBoxesNavigation } from './DataBoxesNavigation';

export const DataBoxesPage = () => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const navigate = useNavigate();

	useTitle(tn('datove-schranky'));

	const { pathname } = useLocation();
	const {
		dataBoxId,
		folderType,
		dataBoxes,
		isDataBoxesLoaded,
		mobileMenuDataBox,
		setMobileMenuDataBox,
		disableNewMessageButton,
		lastOpened,
		isDataBoxConnected,
	} = useDataBoxesContext();

	if (!isDataBoxesLoaded) return null;

	if (dataBoxes.length === 0) {
		return (
			<Navigate
				to={pageRouteResolver(PageCode['dashboard'])}
				replace={true}
			/>
		);
	}

	if (pathname.match(/^\/datove-schranky\/?$/) && dataBoxes.length > 0) {
		const firstConnectedDataBox = dataBoxes.find(
			(dataBox) => dataBox.odpojeno === false
		);
		if (lastOpened && isDataBoxConnected(lastOpened)) {
			return (
				<Navigate
					to={pageRouteResolver('datove-schranky.schranka', {
						dataBoxId: lastOpened,
						folderType: DataBoxFolderType.Received,
					})}
					replace={true}
				/>
			);
		}

		if (firstConnectedDataBox) {
			return (
				<Navigate
					to={pageRouteResolver('datove-schranky.schranka', {
						dataBoxId: firstConnectedDataBox.datovaSchrankaId,
						folderType: DataBoxFolderType.Received,
					})}
					replace={true}
				/>
			);
		}
		return (
			<Navigate
				to={pageRouteResolver('datove-schranky.schranka', {
					dataBoxId: dataBoxes[0].datovaSchrankaId,
					folderType: DataBoxFolderType.Archived,
				})}
				replace={true}
			/>
		);
	}

	return (
		<>
			{mobileMenuDataBox && (
				<GovBackdrop onGov-click={() => setMobileMenuDataBox(null)} />
			)}

			<aside
				className={cx(
					'absolute top-[65px] bottom-0 left-0 px-(--templates-margin-m) pt-(--templates-margin-m) pb-(--templates-margin-xl) w-[72px] flex-col justify-between items-center overflow-x-visible bg-(--background-page) [ sm:top-[73px] ]',
					'[ xl:flex xl:relative xl:top-0 xl:grow-0 xl:shrink-0 xl:w-[228px] xl:p-0! ]',
					mobileMenuDataBox ? 'flex z-[301]' : 'hidden'
				)}>
				<div className="flex flex-col items-center overflow-hidden [ xl:pb-8 xl:items-start xl:gap-(--templates-margin-m) ]">
					<Button
						onClick={() =>
							navigate(
								pageRouteResolver('datove-schranky.schranka.nova-zprava', {
									dataBoxId,
									folderType,
								})
							)
						}
						color="primary"
						type="solid"
						size="m"
						className="hidden! [ xl:block! xl:m-1! ]"
						isDisabled={disableNewMessageButton}
						aria-label={tn('zpravy.wcag.nova-zprava')}
						iconStart={
							<GovIcon
								name="plus-circle"
								type="templates"
							/>
						}>
						{tn('zpravy.nova-zprava')}
					</Button>
					<div className="flex-1 overflow-y-auto scrollbar-gutter-stable">
						<DataBoxStorageUsage />

						<DataBoxesList list={dataBoxes} />
					</div>
				</div>

				<DataBoxesNavigation />
			</aside>
		</>
	);
};
